import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { StatusTone } from "@/lib/statusTone";

/**
 * Card de indicador (KPI) do redesign 2026: rótulo, valor grande em
 * Bricolage, rodapé opcional colorido por tom e ícone opcional à direita.
 * Substitui os `Kpi`/`KpiCard` que viviam duplicados em Financeiro e Linear.
 * Não formata número nem moeda — recebe `value` pronto.
 */
export interface StatTileProps {
  label: string;
  /** Já formatado (moeda, %, contagem). */
  value: ReactNode;
  /** Linha pequena abaixo do valor: contexto, comparação ou alerta. */
  foot?: ReactNode;
  /** Ícone 16px; herda a cor do tom. */
  icon?: ReactNode;
  /** Colore rodapé e ícone. `bad` também colore o valor (é o único tom de alarme). */
  tone?: "default" | StatusTone;
  /** Troca valor e rodapé por skeleton. */
  loading?: boolean;
  className?: string;
}

const TONE_TEXT: Record<NonNullable<StatTileProps["tone"]>, string> = {
  default: "text-muted-foreground",
  neutral: "text-muted-foreground",
  primary: "text-primary",
  info: "text-info",
  ok: "text-ok",
  warn: "text-warn",
  notice: "text-notice",
  bad: "text-bad",
};

export function StatTile({
  label,
  value,
  foot,
  icon,
  tone = "default",
  loading = false,
  className,
}: StatTileProps) {
  const toneText = TONE_TEXT[tone];
  return (
    <div
      className={cn(
        "rounded-[14px] border border-border bg-card px-5 py-[18px] shadow-card",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {icon && (
          <span aria-hidden="true" className={cn("shrink-0", toneText)}>
            {icon}
          </span>
        )}
      </div>
      {loading ? (
        <Skeleton className="mt-[9px] h-8 w-28" />
      ) : (
        <div
          className={cn(
            "mt-[9px] font-display text-2xl font-bold tracking-[-0.035em]",
            tone === "bad" && "text-bad",
          )}
        >
          {value}
        </div>
      )}
      {loading
        ? foot !== undefined && <Skeleton className="mt-[7px] h-3 w-20" />
        : foot !== undefined && (
            <div className={cn("mt-[5px] text-[11.5px] font-semibold", toneText)}>{foot}</div>
          )}
    </div>
  );
}
